import React, { memo } from 'react';
import PropTypes from 'prop-types';
import _maxBy from 'lodash/maxBy';
import _findIndex from 'lodash/findIndex';

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceLine, LabelList,
} from 'recharts';

import styles from './QQScoreHistogramChart.module.css';

const LABEL_HEIGHT = 20;
const LABEL_PADDING_BOTTOM = 5;

const RenderBarLabel = ({ x, y, width, value, showBackgroundRect }) =>
  <g>
    { showBackgroundRect && <rect x={x} y={y - LABEL_HEIGHT} height={LABEL_HEIGHT} width={width} fill="white" /> }
    <text
      x={x + (width / 2)}
      y={y - LABEL_PADDING_BOTTOM}
      fontSize="16"
      fontFamily="Source Sans Pro"
      textAnchor="middle"
    >
      {value}
    </text>
  </g>
;

RenderBarLabel.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  height: PropTypes.number,
  width: PropTypes.number,
  value: PropTypes.number,
  showBackgroundRect: PropTypes.bool,
};

const getRenderBarLabel = averageIndex => (data) => {
  const finalProps = {
    ...data,
    showBackgroundRect: averageIndex === data.index,
  };
  return <RenderBarLabel {...finalProps} />;
};

const renderScoreLabel = (box, value, averageIsInDomainLimit) => {
  const HORIZONTAL_LABEL_MARGIN = 12;
  const VERTICAL_LABEL_MARGIN = averageIsInDomainLimit ? 25 : 5;
  return (
    <text
      fontSize="14"
      fontFamily="Source Sans Pro"
      textAnchor="middle"
      y={box.viewBox.y - VERTICAL_LABEL_MARGIN}
    >
      <tspan
        className={styles.label}
        x={box.viewBox.x - HORIZONTAL_LABEL_MARGIN}
      >
        {`${value}%`}
      </tspan>
      <tspan
        className={styles.labelSmall}
        x={box.viewBox.x + HORIZONTAL_LABEL_MARGIN}
      >
        avg
      </tspan>
    </text>
  );
};

function QQScoreHistogramChart({ data, average, averageRange }) {
  const maxData = _maxBy(data, 'Students');
  const highDomainLimit = maxData.Students + Number(maxData.Students % 2 === 1);
  const averageIsInDomainLimit = maxData.Score === averageRange;
  const indexAverage = _findIndex(data, { Score: averageRange });
  return (
    <ResponsiveContainer height={282}>
      <BarChart
        data={data}
        barCategoryGap={5}
        margin={{ top: 40, right: 30, left: 10, bottom: 65 }}
      >
        <ReferenceLine
          stroke="black"
          strokeDasharray="3 3"
          x={averageRange}
          label={{
            content: box => renderScoreLabel(box, average, averageIsInDomainLimit),
            position: 'top',
            fontFamily: 'Source Sans Pro',
            fontSize: '14',
            fill: '#080808' }}
        />
        <XAxis
          dataKey="Score"
          label={{ value: 'Score ranges',
            position: 'bottom',
            fontSize: 16,
            fontFamily: 'Source Sans Pro',
            fontWeight: 600,
            fill: '#535353',
            dy: 22 }}
          angle={-40}
          tickMargin={15}
          tick={{ fill: 'var(--prs-c-gray-700)' }}
          axisLine={{ stroke: 'var(--prs-c-gray-700)' }}
          tickLine={{ stroke: 'var(--prs-c-gray-700)' }}

        />
        <YAxis
          dataKey="Students"
          width={40}
          domain={[0, highDomainLimit]}
          label={{ value: 'Students',
            position: 'insideLeft',
            angle: -90,
            dx: -7,
            dy: 20,
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'var(--prs-ff-sans',
            fill: 'var(--prs-c-gray-700)' }}
          tick={{ fill: 'var(--prs-c-gray-700)' }}
          axisLine={{ stroke: 'var(--prs-c-gray-700)' }}
          tickLine={{ stroke: 'var(--prs-c-gray-700)' }}

        />
        <CartesianGrid vertical={false} />
        <Bar
          dataKey="Students"
          fill="var(--prs-c-info-800)"
        >
          <LabelList
            dataKey="Students" position="top"
            fontWeight={600}
            fontFamily="Source Sans Pro"
            color="#00FF00"
            content={getRenderBarLabel(indexAverage)}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

QQScoreHistogramChart.propTypes = {
  data: PropTypes.array,
  average: PropTypes.number,
  averageRange: PropTypes.string,
};

export default memo(QQScoreHistogramChart);

